import { UserButton } from '@clerk/clerk-react'
import { UtensilsCrossed, Calendar, ShoppingCart, MessageCircle, User } from 'lucide-react'
import { useAppStore } from '@/store/app'
import ChatAssistant from './ChatAssistant'

export default function Layout({ children }: { children: React.ReactNode }) {
  const { chatOpen, setChatOpen, setPage } = useAppStore()

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top nav */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="w-6 h-6 text-brand-600" />
            <span className="text-xl font-bold text-gray-900">MealMap</span>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            <NavButton icon={<Calendar className="w-4 h-4" />} label="Plan" onClick={() => setPage('dashboard')} />
            <NavButton icon={<ShoppingCart className="w-4 h-4" />} label="Shopping List" onClick={() => setPage('shopping-list')} />
            <NavButton icon={<User className="w-4 h-4" />} label="Preferences" onClick={() => setPage('settings')} />
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setChatOpen(!chatOpen)}
              className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">AI Assistant</span>
            </button>
            <UserButton afterSignOutUrl="/" />
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        </main>

        {/* Chat sidebar */}
        {chatOpen && (
          <aside className="w-96 border-l border-gray-200 bg-white flex flex-col shadow-lg">
            <ChatAssistant onClose={() => setChatOpen(false)} />
          </aside>
        )}
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex">
        <MobileNavButton icon={<Calendar className="w-5 h-5" />} label="Plan" onClick={() => setPage('dashboard')} />
        <MobileNavButton icon={<ShoppingCart className="w-5 h-5" />} label="Shop" onClick={() => setPage('shopping-list')} />
        <button
          onClick={() => setChatOpen(!chatOpen)}
          className="flex-1 flex flex-col items-center gap-1 py-3 text-xs text-brand-600 font-medium"
        >
          <MessageCircle className="w-5 h-5" />
          AI Chat
        </button>
        <MobileNavButton icon={<User className="w-5 h-5" />} label="Settings" onClick={() => setPage('settings')} />
      </nav>
    </div>
  )
}

function NavButton({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
    >
      {icon}
      {label}
    </button>
  )
}

function MobileNavButton({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 flex flex-col items-center gap-1 py-3 text-xs text-gray-500 font-medium hover:text-brand-600"
    >
      {icon}
      {label}
    </button>
  )
}
